import {
  compose,
  pluck,
  range,
  values,
  prop,
  length,
  propOr,
  flatten,
  filter,
  map,
  isEmpty,
  uniq,
} from 'ramda'
import { makeTempId } from 'utils/identity'
import strings from 'utils/strings'
import {
  M0,
  NATIVE_INPUT,
  COMPLEX,
  TIMER,
  CAN_INPUT,
  ANALOG_INPUT,
  DIGITAL_INPUT,
  SWITCH_INPUT,
  SIMPLE,
  ACTIVE_LOW,
  VOLT_0_6,
  NO_EDGE,
} from 'utils/constants'

import {
  CAN_SIXTEEN_DEFAULTS,
  CAN_SINGLE_DEFAULTS,
  CAN_NATIVE_DEFAULTS,
  NATIVE_SINGLE_DEFAULTS,
  NATIVE_SIXTEEN_DEFAULTS,
  SWITCH_SIXTEEN_DEFAULTS,
  SWITCH_SINGLE_DEFAULTS,
  ANALOG_SINGLE_DEFAULTS,
  ANALOG_SIXTEEN_DEFAULTS,
  DIGITAL_SINGLE_DEFAULTS,
  DIGITAL_NATIVE_OUTPUT_DEFAULTS,
  CAN_NATIVE_BOOL_DEFAULTS,
} from 'constants/conditions'

import { OPERATORS, EVAL_VALUES } from 'components/utils'
import { INTERNAL, SERVICE } from 'constants/featureTypes'

export const defaultTimerValues = Object.freeze({
  description: strings.new_timer,
  timerMode: M0,
  initialValue: 0,
  enableResult: true,
  resetResult: true,
  conditions: [],
  queryType: 'timer',
})
export const defaultLogicSetValues = Object.freeze({
  description: strings.new_logic_set,
  finalComplexQueryOperator: 'or',
  abComplexQueryOperator: 'or',
  cdComplexQueryOperator: 'or',
  delayActivate: 0,
  delayDeactivate: 0,
  queryType: 'complex',
  conditions: [],
  countdown: 0,
})

export const defaultLogicNodeProps = {
  [COMPLEX]: defaultLogicSetValues,
  [TIMER]: defaultTimerValues,
}

export const defaultCanInputProps = Object.freeze({
  uri: null,
  canId: '',
  startBit: null,
  numberOfBits: 1,
  description: strings.can_input,
  operator: 2,
  value: 0,
})

export const defaultAnalogInputProps = Object.freeze({
  uri: null,
  operator: 2,
  value: 0,
  inputConfig: VOLT_0_6,
  description: strings.analog_input_pin,
  signal: null,
})

export const defaultSingleInputProps = Object.freeze({
  uri: null,
  operator: 2,
  value: 0,
  signal: null,
})

export const defaultConditionProps = (nodeType) =>
  Object.freeze({
    queryType: 'simple',
    signal: null,
    value: 0,
    result: true,
    hysteresis: 0,
    operator: OPERATORS.EQUAL,
    subType: NATIVE_INPUT,
    refQueryUri: null,
    startBit: null,
    numberOfBits: null,
    canId: null,
    ...(nodeType === COMPLEX && { edge: NO_EDGE }),
  })

export const timerProps = Object.freeze(Object.keys(defaultTimerValues))
export const logicSetProps = Object.freeze(Object.keys(defaultLogicSetValues))
export const canInputProps = Object.freeze(Object.keys(defaultCanInputProps))
export const analogInputProps = Object.freeze(
  Object.keys(defaultAnalogInputProps)
)

const getSingleConditionProps = (
  subType,
  isSixteenBitCan,
  isNativeOutput,
  isOutputBoolean
) => {
  const isCanInput = subType === CAN_INPUT
  const queryType = isCanInput ? CAN_INPUT : SIMPLE

  const subTypeSpecificProps = {
    [NATIVE_INPUT]: isSixteenBitCan
      ? NATIVE_SIXTEEN_DEFAULTS
      : NATIVE_SINGLE_DEFAULTS,
    [SWITCH_INPUT]: isSixteenBitCan
      ? SWITCH_SIXTEEN_DEFAULTS
      : SWITCH_SINGLE_DEFAULTS,
    [DIGITAL_INPUT]: isNativeOutput
      ? DIGITAL_NATIVE_OUTPUT_DEFAULTS
      : DIGITAL_SINGLE_DEFAULTS,
    [ANALOG_INPUT]: isSixteenBitCan
      ? ANALOG_SIXTEEN_DEFAULTS
      : ANALOG_SINGLE_DEFAULTS,
    [CAN_INPUT]: getCanDefaults(
      isSixteenBitCan,
      isNativeOutput,
      isOutputBoolean
    ),
  }

  const defaultProps = {
    [NATIVE_INPUT]: defaultConditionProps(),
    [SWITCH_INPUT]: defaultConditionProps(),
    [DIGITAL_INPUT]: defaultSingleInputProps,
    [ANALOG_INPUT]: defaultAnalogInputProps,
    [CAN_INPUT]: defaultCanInputProps,
  }[subType]

  return {
    ...defaultProps,
    ...subTypeSpecificProps[subType],
    ...(isNativeOutput && {
      value: EVAL_VALUES.TRUE,
      operator: OPERATORS.EQUAL,
    }),
    queryType,
  }
}

export const DIGITAL_INPUT_CONFIG_DEFAULT = {
  inputConfig: ACTIVE_LOW,
  dataType: DIGITAL_INPUT,
}

export const ANALOG_INPUT_CONFIG_DEFAULT = {
  inputConfig: VOLT_0_6,
  dataType: ANALOG_INPUT,
}

/* eslint-disable no-nested-ternary */
export const resourcesRemainingIndicatorColor = (proportion, theme) =>
  proportion === 1
    ? theme.vehicleFullColor
    : proportion >= 0.75
    ? theme.vehicleNearlyFullColor
    : theme.vehicleSpaceAvaliableColor
/* eslint-enable no-nested-ternary */

const getCanDefaults = (
  isSixteenBitCan,
  isNativeOutputType,
  isOutputBoolean
) => {
  if (isSixteenBitCan) return CAN_SIXTEEN_DEFAULTS
  if (isNativeOutputType && isOutputBoolean) return CAN_NATIVE_BOOL_DEFAULTS
  if (isNativeOutputType) return CAN_NATIVE_DEFAULTS
  return CAN_SINGLE_DEFAULTS
}

export const makeSingleCondition = (
  subType,
  fnId,
  isSixteenBitCan,
  isNativeOutput,
  isOutputBoolean
) => {
  const id = makeTempId('singleCondition')
  const conditionProps = getSingleConditionProps(
    subType,
    isSixteenBitCan,
    isNativeOutput,
    isOutputBoolean
  )

  return {
    ...conditionProps,
    uri: `queries/${id}`,
    fnId,
  }
}

const makeCondition = (nodeType, parentQueryUri, index) => {
  const id = makeTempId('condition')

  return {
    id,
    uri: `queries/${id}`,
    parentQueryUri,
    parentType: nodeType,
    conditionIndex: index,
    ...defaultConditionProps(nodeType),
  }
}

const makeConditions = (nodeType, parentQueryUri) => {
  const numConditions = nodeType === COMPLEX ? 4 : 2

  return range(0, numConditions).reduce((acc, rangeIndex) => {
    const condition = makeCondition(nodeType, parentQueryUri, rangeIndex)
    return {
      ...acc,
      [condition.uri]: condition,
    }
  }, {})
}

const getUris = compose(pluck('uri'), values)

export const makeLogicNode = (nodeType, index) => {
  const id = makeTempId(nodeType)
  const uri = `queries/${id}`

  const conditions = makeConditions(nodeType, uri)
  const conditionUris = getUris(conditions)

  const defaultProps = defaultLogicNodeProps[nodeType]

  return {
    logicNode: {
      ...defaultProps,
      uri,
      id,
      description: `${defaultProps.description} ${index}`,
      conditions: conditionUris,
    },
    conditions,
  }
}

export const filterOutUsedSwitches = (removedSwitches, features) => {
  if (isEmpty(removedSwitches)) return removedSwitches

  const switchesUsedInOtherFeatures = features.reduce((acc, curr) => {
    if (!curr.isMarkedForDeletion) {
      const { ioSwitches = [] } = curr
      const switchesOnVehicle = ioSwitches.reduce((acc, { switches = [] }) => {
        const onVehicle = switches.reduce((acc, curr) => {
          if (curr.isSwitchOnVehicle) {
            return [...acc, curr.panelNumber]
          }
          return acc
        }, [])

        return [...acc, ...onVehicle]
      }, [])

      return [...acc, ...switchesOnVehicle]
    }
    return acc
  }, [])

  return removedSwitches.filter(
    (removedSwitch) => !switchesUsedInOtherFeatures.includes(removedSwitch)
  )
}

export const getRemovedSwitches = (features, keepOrRemoveSwitches) => {
  const removedSwitches = features.reduce((acc, curr) => {
    const { ioSwitches = [] } = curr

    const removed = compose(
      filter((x) => x.isSwitchOnVehicle),
      flatten,
      map(prop('switches'))
    )(ioSwitches)

    if (length(removed)) return [...acc, ...removed]
    return acc
  }, [])

  const list = uniq(filterOutUsedSwitches(removedSwitches, features))

  const panelNumbersMarkedForDeletion = keepOrRemoveSwitches
    .reduce((acc, curr) => {
      const { switches = [] } = curr
      return [...acc, ...switches]
    }, [])
    .filter(prop('isMarkedForDeletion'))
    .map(prop('panelNumber'))

  const removedSwitchList = list.filter((sw) =>
    panelNumbersMarkedForDeletion.includes(prop('panelNumber', sw))
  )

  return removedSwitchList
}

export const getAddedSwitches = (
  features,
  selectedSwitches,
  isForUpdatedFeatures
) => {
  const addedSwitches = features.reduce((acc, curr) => {
    const { ioSwitches = [] } = curr

    const added = ioSwitches
      .map((inputs) => {
        const { switches = [], id } = inputs

        // if there is only 1 switch, it is the default getting added
        if (length(switches) === 1 && !isForUpdatedFeatures)
          return prop(0, switches)

        // if there are more than 1, then we need to show which switch the user
        // choose to add
        const selectedSwitch = switches.find(
          ({ panelNumber }) => panelNumber === prop(id, selectedSwitches)
        )

        // If no user selection exists (e.g. Feature Collections flow where
        // mappedIoSwitches is empty), auto-select the first available switch
        if (
          !selectedSwitch &&
          !isForUpdatedFeatures &&
          !(id in selectedSwitches)
        )
          return prop(0, switches)

        return selectedSwitch
      })
      .filter((sw) => sw && !sw.isSwitchOnVehicle)

    if (added.length) return [...acc, ...added]
    return acc
  }, [])

  return uniq(addedSwitches)
}

export const getRemovedSwitchesFromPrevSnapshot = (
  features,
  removedSwitches
) => {
  const addedSwitches = features.reduce((acc, curr) => {
    const { ioSwitches = [] } = curr.previousSnapshot

    const removed = ioSwitches
      .map((inputs) => {
        const { switches = [], id } = inputs

        const removedSwitch = switches.find(
          ({ panelNumber }) => panelNumber === prop(id, removedSwitches)
        )

        return removedSwitch
      })
      .filter((sw) => sw) // remove any undefineds

    if (removed.length) return [...acc, ...removed]
    return acc
  }, [])

  return uniq(addedSwitches)
}

export const getIOSwitchList = (selectedSearchFeature) => {
  const response = {
    selectedSwitches: {},
    ioSwitches: [],
  }

  const ioSwitches = propOr([], 'ioSwitches', selectedSearchFeature)

  return ioSwitches.reduce((acc, curr) => {
    const switches = propOr([], 'switches', curr)

    if (length(switches) === 1) {
      const featureId = prop('id', curr)
      const switchNumber = prop('panelNumber', switches[0])
      acc.selectedSwitches[featureId] = switchNumber
      return acc
    }

    acc.ioSwitches.push({
      ...curr,
      featureId: selectedSearchFeature.id,
    })

    return acc
  }, response)
}

export const isValidFeatureMetaData = (
  type,
  priority,
  subGroup,
  releaseNumber,
  description
) => {
  // valid desc
  const isValidDesc = !!description && !(description.length > 80)
  // valid internal type feature
  const isInternalFeature = type === INTERNAL
  const isValidInternal =
    isInternalFeature &&
    priority >= 0 &&
    priority <= 100 &&
    releaseNumber.length === 9 &&
    !!subGroup &&
    isValidDesc
  // valid service type feature
  const isServiceFeature = type === SERVICE
  const isValidService = isServiceFeature && isValidDesc
  return isValidInternal || isValidService
}

/**
 * Formats the minimum diagnostic variant to the standard "App_0###" format.
 * Handles both the legacy "App_0###" format and the hex "0x0###" format.
 *
 * @param {string} variant - The variant string to format
 * @returns {string} The formatted variant in "App_0###" format, or the original value if null/undefined
 *
 * @example
 * formatMinDiagnosticVariant('0x0706') // returns 'App_0706'
 * formatMinDiagnosticVariant('App_0706') // returns 'App_0706'
 * formatMinDiagnosticVariant(null) // returns null
 */
export const formatMinDiagnosticVariant = (variant) => {
  if (!variant) return variant
  // If already in App_#### format, return as-is
  if (variant.startsWith('App_')) return variant
  // Convert 0x#### to App_####
  if (variant.startsWith('0x')) {
    return `App_${variant.slice(2)}`
  }
  return variant
}

/**
 * Parses a diagnostic variant string into a numeric value for comparison.
 * Handles "App_XXXX" and "0xXXXX" formats where XXXX is hexadecimal.
 *
 * @param {string} variant - The variant string (e.g., "App_0706", "0x0706")
 * @returns {number|null} The parsed numeric value, or null if unparseable
 */
const parseVariantNumber = (variant) => {
  if (!variant) return null
  const normalized = formatMinDiagnosticVariant(variant)
  if (!normalized || !normalized.startsWith('App_')) return null
  const hexPart = normalized.slice(4)
  const num = parseInt(hexPart, 16)
  return Number.isNaN(num) ? null : num
}

/**
 * Checks whether a vehicle ECU's app version is compatible with a feature's
 * minimum required variant. The ECU variant must be >= the feature's minimum.
 *
 * @param {string} ecuVariant - The vehicle ECU's variant (e.g., "App_0115")
 * @param {string} featureMinVariant - The feature's minimum variant (e.g., "0x0116")
 * @returns {boolean} true if compatible (ECU >= feature min), or if either value is missing/unparseable
 *
 * @example
 * isVariantCompatible('App_0115', '0x0111') // true  (0x0115 >= 0x0111)
 * isVariantCompatible('App_0115', '0x0116') // false (0x0115 < 0x0116)
 * isVariantCompatible('App_0115', null)     // true  (missing data, assume compatible)
 */
export const isVariantCompatible = (ecuVariant, featureMinVariant) => {
  const ecuNum = parseVariantNumber(ecuVariant)
  const featureNum = parseVariantNumber(featureMinVariant)
  // If either value is missing or unparseable, assume compatible
  if (ecuNum === null || featureNum === null) return true
  return ecuNum >= featureNum
}
